import React, { useEffect, createElement } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CtaButton } from '../components/CtaButton';
import { getPost, posts } from '../data/posts';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = slug ? getPost(slug) : undefined;
  // Scroll to the single booking form (in the footer) without leaving the page.
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!post) return;
    document.title = post.metaTitle;
    // Keep a single managed meta-description tag in sync for SEO.
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', post.metaDescription);
    // Article + FAQ JSON-LD schema for rich results.
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.setAttribute('data-post-schema', post.slug);
    ld.textContent = JSON.stringify([{
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.metaDescription,
      author: {
        '@type': 'Organization',
        name: 'Yetu Design Lab'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Yetu Design Lab'
      },
      keywords: [post.keyphrase, ...post.keywords].join(', ')
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faqs.map(f => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer
        }
      }))
    }]);
    document.head.appendChild(ld);
    return () => {
      ld.remove();
    };
  }, [post]);
  if (!post) {
    return <Navigate to="/blog" replace data-id="element-330" />;
  }
  const related = post.related.map(s => getPost(s)).filter((p): p is NonNullable<typeof p> => Boolean(p));
  return <>
      <div className="glow" data-id="element-331"></div>
      <Navbar data-id="element-332" />

      <main className="wrap" data-id="element-333">
        <article className="sec pb-0" data-id="element-334">
          <Link to="/blog" className="mono text-xs uppercase tracking-wider text-mut hover:text-lime transition-colors inline-flex items-center gap-2 mb-10" data-id="element-335">
            <ArrowLeft size={14} data-id="element-336" /> All insights
          </Link>

          <header className="max-w-[72ch]" data-id="element-337">
            <div className="flex flex-wrap items-center gap-3 mono text-xs uppercase tracking-wider mb-6" data-id="element-338">
              <span className="text-lime" data-id="element-339">{post.category}</span>
              <span className="text-mut-2" data-id="element-340">•</span>
              <span className="text-mut-2" data-id="element-341">{post.author}</span>
              <span className="text-mut-2" data-id="element-342">•</span>
              <span className="text-mut-2" data-id="element-343">{post.date}</span>
              <span className="text-mut-2" data-id="element-344">•</span>
              <span className="text-mut-2" data-id="element-345">{post.readTime}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] mb-8" data-id="element-346">
              {post.title}
            </h1>
            <p className="text-xl md:text-2xl text-ink/90 leading-relaxed serif not-italic" data-id="element-347">
              {post.intro}
            </p>
          </header>
        </article>

        <article className="sec pt-12" data-id="element-348">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-12 lg:gap-16 items-start" data-id="element-349">
            {/* Article body */}
            <div className="max-w-[72ch] text-lg leading-relaxed text-mut space-y-10" data-id="element-350">
              {post.sections.map((section, i) => <section key={i} className="space-y-5" data-id="element-351">
                  {section.heading && <h2 className="text-2xl md:text-3xl font-bold text-ink tracking-tight" data-id="element-352">
                      {section.heading}
                    </h2>}
                  {section.paragraphs.map((p, j) => <p key={j} data-id="element-353">{p}</p>)}
                  {section.bullets && <ul className="space-y-3 pl-0" data-id="element-354">
                      {section.bullets.map((b, k) => <li key={k} className="flex gap-3" data-id="element-355">
                          <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-lime shrink-0" aria-hidden="true" data-id="element-356" />
                          <span data-id="element-357">
                            {b.term && <strong className="text-ink font-semibold" data-id="element-358">
                                {b.term}:{' '}
                              </strong>}
                            {b.text}
                          </span>
                        </li>)}
                    </ul>}
                </section>)}

              {/* Contextual internal links */}
              <section className="space-y-5 pt-2" data-id="element-359">
                <h2 className="text-2xl md:text-3xl font-bold text-ink tracking-tight" data-id="element-360">
                  Keep going
                </h2>
                <p data-id="element-361">
                  Explore how we put this into practice in our{' '}
                  <Link to="/#work" className="text-lime underline underline-offset-4 hover:opacity-80" data-id="element-362">
                    selected work
                  </Link>
                  {post.caseStudy ? <>
                      {' '}
                      — or read the{' '}
                      <Link to={post.caseStudy.to} className="text-lime underline underline-offset-4 hover:opacity-80" data-id="element-363">
                        {post.caseStudy.label.replace('See the ', '')}
                      </Link>
                    </> : <>
                      {' '}
                      — or learn more about{' '}
                      <Link to={post.serviceAnchor} className="text-lime underline underline-offset-4 hover:opacity-80" data-id="element-364">
                        how the studio works
                      </Link>
                    </>}
                  .
                </p>
              </section>
            </div>

            {/* Sticky lead-capture sidebar CTA (right margin) */}
            <aside className="hidden lg:block" data-id="element-365">
              <div className="sticky top-24 border-l-2 border-lime bg-raise rounded-r-lg p-7" data-id="element-366">
                <div className="eyebrow mb-3" data-id="element-367">Free consultation</div>
                <h3 className="text-xl font-bold text-ink mb-2 leading-snug" data-id="element-368">
                  Want this working for your business?
                </h3>
                <p className="text-mut mb-6 text-sm leading-relaxed" data-id="element-369">
                  We'll map the fastest path to the growth you're after — brand,
                  web, or both.
                </p>
                <CtaButton as="button" onClick={scrollToContact} variant="pill" className="w-full justify-center" data-id="element-370">
                  Book Free Consultation
                </CtaButton>
              </div>
            </aside>
          </div>
        </article>

        {/* Related articles */}
        {related.length > 0 && <section className="sec border-t border-line pt-16" data-id="element-371">
            <div className="eyebrow mb-8" data-id="element-372">Related articles</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-373">
              {related.map(r => <Link key={r.slug} to={`/blog/${r.slug}`} className="group border border-line rounded-r-lg bg-raise p-7 flex flex-col gap-3 transition-all hover:border-line-2 hover:-translate-y-1" data-id="element-374">
                  <div className="flex items-center gap-3 mono text-xs uppercase tracking-wider" data-id="element-375">
                    <span className="text-lime" data-id="element-376">{r.category}</span>
                    <span className="text-mut-2" data-id="element-377">•</span>
                    <span className="text-mut-2" data-id="element-378">{r.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold tracking-tight group-hover:text-lime transition-colors" data-id="element-379">
                    {r.title}
                  </h3>
                  <span className="mono text-xs uppercase tracking-wider text-ink inline-flex items-center gap-1.5 group-hover:text-lime transition-colors" data-id="element-380">
                    Read more <ArrowRight size={14} data-id="element-381" />
                  </span>
                </Link>)}
            </div>
          </section>}
      </main>

      <Footer data-id="element-382" />
    </>;
}